import { staticText } from '../../utils/translations/staticText';

/**
 * The "Your details" group and the eligibility outcomes (CMS-1905).
 *
 * Split from `i18n.ts` because the copy varies by locale rather than by the
 * `_`/`zh` split the panel chrome uses: Hong Kong says "Residency status" where
 * Singapore says "Citizenship", and the two Chinese locales address the reader
 * differently (你 in ZH-HK, 您 in ZH-TW). `resolveLocale` tries the exact
 * locale, then its two-letter prefix, then `_`, so naming `en-hk`, `zh-hk` and
 * `zh-tw` is enough — no brand overlay is needed.
 *
 * The citizenship *options* are not here on purpose: they are the values PCT
 * entered on the products, shown exactly as entered (AC 2e).
 */
export const ELIGIBILITY_TRANSLATIONS = staticText({
  eligibility_group_title: {
    _: 'Your details',
    'zh-hk': '你的資料',
    'zh-tw': '您的資料',
  },
  eligibility_citizenship_label: {
    _: 'Citizenship',
    'en-hk': 'Residency status',
    'zh-hk': '居留身份',
    'zh-tw': '國籍',
  },
  // Shown on the control before a choice is made. Distinct from the heading
  // above it, which the control already takes its accessible name from —
  // repeating it there would have screen readers announce it twice.
  eligibility_citizenship_placeholder: {
    _: 'Select',
    'zh-hk': '請選擇',
    'zh-tw': '請選擇',
  },
  eligibility_income_label: {
    _: 'Monthly income',
    'zh-hk': '每月收入',
    'zh-tw': '每月收入',
  },
  eligibility_meets: {
    _: 'You meet the basic criteria',
    'zh-hk': '你符合基本申請條件',
    'zh-tw': '您符合基本申請條件',
  },
  // `{income}` is the lender's own published minimum, formatted in the market's
  // currency. The ticket writes it as a DVR variable, which names one product;
  // this message is per loan, so the figure is read off each product instead.
  /**
   * ⚠️ **Deviates from the ticket's copy table, deliberately.** The table reads
   * "Usually needs `<DVR variable>` income" with no period attached, but the
   * figure is the lender's *annual* minimum while the field the reader just
   * filled in is labelled "Monthly income" — so someone entering 2,500 read
   * "Usually needs S$30,000 income", two numbers 12x apart with nothing saying
   * so. UC 3 is the requirement that names the mismatch. Raised in review and
   * on the ticket; the wording here is engineering's until PCT corrects it.
   *
   * The Chinese uses 年收入 ("annual income"), which carries the period in the
   * noun rather than needing a trailing qualifier.
   */
  eligibility_short_income: {
    _: 'Usually needs {income} a year. You can still apply, the lender decides.',
    'zh-hk': '一般需要年收入 {income}。你仍可申請，最終由貸款機構決定。',
    'zh-tw': '通常需要年收入 {income}。您仍可申請，最終由貸款機構決定。',
  },
  eligibility_short_citizenship: {
    _: 'May not be available for your citizenship. You can still apply, the lender decides.',
    'en-hk':
      'May not be available for your residency status. You can still apply, the lender decides.',
    'zh-hk': '此貸款未必適用於你的居留身份。你仍可申請，最終由貸款機構決定。',
    'zh-tw': '此貸款未必適用於您的國籍。您仍可申請，最終由貸款機構決定。',
  },
  // The second half of the results line ("21 personal loans found · 14 you
  // meet the criteria for"); the first half is the panel's own count.
  eligibility_count_eligible: {
    _: '{count} you meet the criteria for',
    'zh-hk': '其中 {count} 項符合你的基本條件',
    'zh-tw': '其中 {count} 項符合您的基本條件',
  },
  /**
   * The comparison table's compact trigger: the full reason would wrap to five
   * lines in a column sized for a button, so the table shows this and carries
   * the reason in the tooltip. The card still shows the sentence in full.
   *
   * ⚠️ English only — this string is not in the ticket's copy table, so it has
   * no approved ZH-HK / ZH-TW wording yet and those locales fall back to the
   * English. Asked of the ticket owner 2026-09-15.
   */
  eligibility_check_label: {
    _: 'Check eligibility',
  },
  eligibility_disclaimer: {
    _: "Based on each lender's published minimum criteria. Actual criteria may differ, and this is not an approval decision.",
    'zh-hk':
      '以上結果根據各貸款機構公佈的基本申請條件，實際條件可能不同，並不代表批核結果。',
    'zh-tw':
      '以上結果依各貸款機構公告的基本申請條件，實際條件可能不同，並不代表核准結果。',
  },
});

export type EligibilityTranslations = typeof ELIGIBILITY_TRANSLATIONS;
